import { useEffect, useRef, useState } from 'react';

/**
 * Seconds elapsed since `active` last flipped to true. Resets to 0 whenever
 * `active` is false. Ticks every 500ms so the label feels live without busy-work.
 *
 * Extracted from the per-country extraction tickers in ResearchCompanyDialog
 * (see :354-368) so SmartLoader and future loaders share one implementation.
 */
export function useElapsedSeconds(active: boolean): number {
  const [elapsedSec, setElapsedSec] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      setElapsedSec(0);
      startRef.current = null;
      return;
    }
    if (startRef.current === null) startRef.current = Date.now();
    const interval = setInterval(() => {
      if (startRef.current !== null) {
        setElapsedSec(Math.floor((Date.now() - startRef.current) / 1000));
      }
    }, 500);
    return () => clearInterval(interval);
  }, [active]);

  return elapsedSec;
}

/** `2m 05s` / `45s` — matches the elapsed label format used across the app. */
export function formatElapsed(totalSec: number): string {
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
}
